import { useTranslation } from 'react-i18next';
import React, { useState, useContext, useEffect } from 'react';
import { VIEW, EDIT, COPY, DELETE, UPDATE, NEW, UNLOCK, REPORTS } from '../../assets/Constants/actionsTypes';
import Notifications, { ERROR_NOTIFICATION, SUCCESS_NOTIFICATION } from '../../helpers/notifications';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useHistory } from "react-router-dom";
import './model.css';
// * Model grid component
import ModelGrid from './modelGrid'
// * Model modal
import ModelModal from './modelModal'
// * Helpers
import validator from 'validator';
import API from "../../services/api";
import AuthContext from '../../context/Auth/AuthContext';

function Model() {

    /**We use the useTranslation hook to update the text when the language changes */
    const { t } = useTranslation('common');
    /**The useHistory hook allow us to redirect to another route */
    const history = useHistory();
    // Modal Window type
    const [viewType, setViewType] = useState('');
    // Single component
    const [modelData, setModelData] = useState({});
    // List of models. See useEffect
    const [modelsList, setModelsList] = useState([]);
    /**State of the modal window. True = showing ... False = hidden */
    const [showModal, setShowModal] = useState(false);
    /** State of the dialog. True = showing ... False = hidden */
    const [showAlert, setShowAlert] = useState(false);
    // Session context to get the current institution data
    const { get_currentInstitution, user } = useContext(AuthContext);

    // we use useEffect to call the function that fills the grid
    useEffect(() => {
        getModelsList();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Triggered when the user clicks an action in the grid. Opens the modal window according to the selection
    const onActionClick = (actionItem, dataItme) => {
        const action = actionItem.value;
        setViewType(action);
        setModelData(dataItme);

        switch (action) {
            case NEW:
                toggleWindow();
                break;
            case VIEW:
                history.push({
                    pathname: '/Wizard',
                    state: {
                        detail: dataItme,
                        action: VIEW
                    }
                });
                break;
            case UPDATE:
                toggleWindow();
                break;
            case EDIT:
                onEdit(dataItme);
                break;
            case COPY:
                toggleAlert();
                break
            case DELETE:
                toggleAlert();
                break;
            case UNLOCK:
                onUnlock(dataItme);
                break;
            case REPORTS:
                history.push({
                    pathname: '/ModelReports',
                    state: {
                        detail: dataItme
                    }
                });
                break
            default:
                break;
        }
    }

    const getModelsList = async () => {
        setModelsList([]);
        try {
            // data from API
            const response = await API.get(`/model/getAll?institutionId=${get_currentInstitution().catInstitutionId}`);
            setModelsList(response.data.data === null ? [] : response.data.data);
        } catch (e) {
            Notifications.notify(String(e), ERROR_NOTIFICATION);
            console.log(e);
        }
    }

    // Save button
    const onSave = async (data) => {
        try {
            // Vaidations
            if (validator.isEmpty(data.name) || validator.isEmpty(data.code)) {
                Notifications.notify(t('model.name_code_required'), ERROR_NOTIFICATION);
                return;
            }

            data = {
                modelid: data.modelid,
                institutionId: get_currentInstitution().catInstitutionId,
                name: data.name,
                code: data.code
            }

            // * call the api to do the upsert
            const res = await API.post('/model/upsert', data);

            // * if errors exist, it is displayed as a notification
            if (res.data.code !== 100) {
                Notifications.notify(t(`model.${res.data.code}`), ERROR_NOTIFICATION);
                return;
            }

            // * we decide what kind of message of success we should give
            const msg = data.modelid === 0 ? t('model.new_message') : t('model.update_message');

            // * hide the modal, shows success message and refresh the grid
            toggleWindow();
            Notifications.notify(msg, SUCCESS_NOTIFICATION);
            getModelsList();

        } catch (e) {
            Notifications.notify(String(e), ERROR_NOTIFICATION);
            console.log(e)
        }
    }

    //* When user clicks ok in delete modal
    const onDelete = async () => {
        try {
            const haveScenarios = await API.get(`/model/haveScenarios?modelid=${modelData.modelid}`);
            // * if errors exist, it is displayed as a notification
            if (haveScenarios.data.code !== 100) {
                Notifications.notify(t(`model.${haveScenarios.data.code}`), ERROR_NOTIFICATION);
                return;
            }
            if (!haveScenarios.data.data) {
                // * call the api to do the delete
                const res = await API.delete(`/model?modelid=${modelData.modelid}&usercode=${user.email}`);
                // * if errors exist, it is displayed as a notification
                if (res.data.code !== 100) {
                    Notifications.notify(t(`model.${res.data.code}`), ERROR_NOTIFICATION);
                    return;
                }
                // * hide the alert and refresh the grid
                toggleAlert();
                Notifications.notify(t('model.delete_message'), SUCCESS_NOTIFICATION);
                getModelsList();
            } else {
                Notifications.notify(t(`model.500003`), ERROR_NOTIFICATION);
            }
        } catch (e) {
            Notifications.notify(String(e), ERROR_NOTIFICATION);
            console.log(e)
        }
    }

    // * When user clicks Edit in one model
    const onEdit = async (modelData) => {
        // **** If editLockUserCode is null then the model can be edited by the user.
        if (modelData.editLockUserCode === null) {

            // * call the api to lock the model
            const res = await API.post(`/model/lockModel?modelId=${modelData.modelid}&userCode=${user.email}`);

            // * if errors exist, it is displayed as a notification
            if (res.data.code !== 100) {
                Notifications.notify(t('model.lockModelError'), ERROR_NOTIFICATION);
                return;
            } else {
                // * If all ok, the system shows the wizard
                history.push({
                    pathname: '/Wizard',
                    state: {
                        detail: modelData,
                        action: EDIT
                    }
                });
            }
        } else if (modelData.editLockUserCode === user.email) {
            // **** If the model is already locked by the user, the user only enters without updating the DB.
            history.push({
                pathname: '/Wizard',
                state: {
                    detail: modelData,
                    action: EDIT
                }
            });
        } else {
            // **** If not, error is displayed
            Notifications.notify(t('model.inEditMode', { user: modelData.editLockUserCode }), ERROR_NOTIFICATION);
        }
    }

    // * When user clicks UNLOCK in one model
    const onUnlock = async (modelData) => {
        try {
            // * call the api to unlock the model
            const res = await API.post(`/model/unlockModel?modelId=${modelData.modelid}&userCode=${user.email}`);

            // * if errors exist, it is displayed as a notification
            if (res.data.code !== 100) {
                Notifications.notify(t('model.unlockModelError'), ERROR_NOTIFICATION);
                return;
            }

            Notifications.notify(t('model.unlock_message'), SUCCESS_NOTIFICATION);
            getModelsList();
        } catch (e) {
            Notifications.notify(String(e), ERROR_NOTIFICATION);
            console.log(e)
        }
    }

    //* When user clicks ok in copy modal
    const onCopy = async () => {
        try {
            // * call the api to unlock the model
            const res = await API.post(`/model/modelCopy?modelId=${modelData.modelid}`);

            // * if errors exist, it is displayed as a notification
            if (res.data.code !== 100) {
                Notifications.notify(t('model.copyError'), ERROR_NOTIFICATION);
                return;
            }

            // * hide the alert and refresh the grid
            toggleAlert();
            Notifications.notify(t('model.copySuccess'), SUCCESS_NOTIFICATION);
            getModelsList();
        } catch (e) {
            Notifications.notify(String(e), ERROR_NOTIFICATION);
            console.log(e)
        }
    }

    // Show or hide the modal window. See state variable
    const toggleWindow = () => {
        setShowModal(!showModal);
    }

    // Show or hide the alert dialog. See state variable
    const toggleAlert = () => {
        setShowAlert(!showAlert);
    }

    // Shows the Alert body according to the selected action 
    const showAlertBody = () => {
        if (viewType === DELETE)
            return t('model.delete_body')
        else if (viewType === COPY)
            return t('model.copy_body')
    }

    // Shows the Alert title according to the selected action 
    const showAlertTitle = () => {
        if (viewType === DELETE)
            return t('model.delete_title')
        else if (viewType === COPY)
            return t('model.copy_title')
    }

    // Decides which function to call according to the selected action
    const actionOrchestrator = () => {
        if (viewType === DELETE)
            onDelete()
        else if (viewType === COPY)
            onCopy()
    }

    return (
        <>
            <div className="sub_menu">
                <div className="sub_menu_btns my-1">
                    <button className="btn btn-primary ilo-btn" onClick={() => onActionClick({ value: NEW }, {})}>{t('model.new_button')}</button> | <span className="ilo-section-title p-1">{t('model.modalTitle')}</span>
                </div>
            </div>
            <div className="container-fluid mt-1">
                {/* modal window */}
                {showModal && <ModelModal type={viewType} data={modelData} toggleWindow={toggleWindow} onSave={onSave} />}

                {/* alert window */}
                {showAlert &&
                    <Dialog
                        className="ilo-delete-confirm"
                        title={showAlertTitle()}
                        onClose={() => toggleAlert()}
                    >
                        <p style={{ margin: "25px", textAlign: "center" }}>{showAlertBody()}</p>
                        <DialogActionsBar layout="end">
                            <button className="k-button" onClick={actionOrchestrator}>{t('alerts.yes_btn')}</button>
                            <button className="k-button" onClick={toggleAlert}>{t('alerts.no_btn')}</button>
                        </DialogActionsBar>
                    </Dialog>
                }

                {/* grid */}
                <div className="row">
                    <div className="container_grid">
                        <ModelGrid data={modelsList} onActionClick={onActionClick} />
                    </div>
                </div>
            </div>
        </>

    );
}

export default Model